import React, { Component } from 'react';
import { connect, } from 'react-redux';
import axios from 'axios';
import { Container, Spinner, Form } from 'react-bootstrap';
import FormAlert from 'components/Forms/FormAlert';
import { withRouter } from '../withRouter';
import { joinRoom, reconnectToRoom, joinAsMirror } from 'actions/room';
import PageSEO from 'components/Utlility/PageSEO';
import { SEO_CONFIG } from 'constants/SeoConfig';

import ErrorModal from '../Utlility/ErrorModal';
import DotLoader from 'components/Utlility/DotLoader';
import LogoWhite from 'images/logo-white.png';

// Will need to refactor form styling into forms.scss as its become a bit of a mess
import formStyles from "components/Forms/FormStyles.module.scss"
import styles from './Join.module.scss';
import AutoJoinModal from '../Roku/AutoJoinModal/AutoJoinModal';

// "PLAYER" was historically the silent default when a player submitted with no
// nickname — every nameless join became "PLAYER" in the lobby, making it
// impossible to tell guests apart. Treat any submission of "PLAYER" (any
// casing, with surrounding whitespace) as if the field were empty so the
// player gets prompted to enter a real name.
function isValidPlayerName(name) {
    if (!name) return false;
    const trimmed = String(name).trim();
    if (trimmed.length === 0) return false;
    if (trimmed.toUpperCase() === "PLAYER") return false;
    return true;
}

// Rotating waiting copy used by waitForRoomAndJoin's retry loop. Two flavours:
// RETURNING is shown when the server told us the room is coming back from an
// external game (host is recreating the lobby — usually quick, but slow on
// throttled hardware). SEARCHING is shown when the room simply isn't there
// yet (player arrived ahead of host on a join link).
const RETURNING_MESSAGES = [
    "Getting the lobby ready...",
    "Plumping up the cushions...",
    "Brushing off potato crumbs...",
    "Putting the kettle on...",
    "Lining up the next game...",
    "Tuning the buzzers...",
    "The host is finding the rules...",
    "Reassembling the deck...",
    "Polishing the dice...",
    "Almost there...",
];
const SEARCHING_MESSAGES = [
    "Looking for your room...",
    "Knocking on doors...",
    "Checking under the cushions...",
    "Asking around the lobby...",
    "Hunting down the host...",
];
// Hold each message on screen for a few attempts so users have time to read it
// before it changes — single-second rotation feels frantic.
const MESSAGE_HOLD_ATTEMPTS = 3;

// Absolute ceiling for the auto-action screen (reconnecting / returning to
// lobby). waitForRoomAndJoin gives up after its own retry budget, but a hung
// game-exists request, a reconnect that never resolves, or any missed exit
// path would otherwise leave players staring at the screen forever. Set above
// the retry loop's ~60s so the loop's friendlier error normally wins.
const AUTO_ACTION_TIMEOUT_MS = 75 * 1000;
function pickWaitingMessage(attempt, isReturning) {
    const pool = isReturning ? RETURNING_MESSAGES : SEARCHING_MESSAGES;
    const idx = Math.floor((attempt - 1) / MESSAGE_HOLD_ATTEMPTS) % pool.length;
    return pool[idx];
}

class Join extends Component {
    static displayName = Join.name;

    constructor(props) {
        super(props);

        this.state = {
            roomId: "",
            sessionId: "",
            loading: false,
            nickname: "",
            errorMessage: "",
            checkingRoomOpened: true,
            connectionError: false,
            canJoin: false,
            joinButtonValue: "JOIN",
            QRSessionID: new URLSearchParams(window.location.search).get("QRSessionID"),
            token: "",
            passwordProtected: false,
            pin: "",
            errorVariant: "danger",
            autoActionMode: null,      // 'reconnecting' | 'waiting_for_host' | null
            autoActionStatus: '',      // Status message to display
            reconnectFailed: false,    // If true, forces "Join" button instead of "Reconnect"
        };
        this.cancelToken = null;
    }

    async componentDidMount() {
        // get nickname from local storage. Don't pre-fill if it's the legacy
        // "PLAYER" placeholder — that ensures the user has to type a real name
        // even if their device has a stale default saved.
        //
        // Captured into a local var because setState is async and the qrCode
        // branch below needs to know — synchronously — whether we have a
        // valid name. Reading this.state.nickname there would see the
        // pre-mount empty string and skip auto-join even when a name exists.
        let savedNickname = "";
        try {
            const stored = localStorage.getItem("nickname");
            if (stored && isValidPlayerName(stored)) {
                savedNickname = stored;
                this.setState({ nickname: stored });
            } else if (stored) {
                // Stale "PLAYER" default — clear it so it doesn't sneak through later
                localStorage.removeItem("nickname");
            }
        } catch (e) {
            console.log(e);
        }

        const queryParams = new URLSearchParams(window.location.search);

        if (this.props.room.roomOpen) {
            if (this.props.room.isHost) {
                return setTimeout(() => this.props.navigate("/lobby"));
            } else {
                if (queryParams.has("roku")) {
                    return setTimeout(() => this.props.navigate(`/play?roku`));
                } else {
                    return setTimeout(() => this.props.navigate(`/play`));
                }
            }
        } else {
            this.setState({ checkingRoomOpened: false });
            if (queryParams.has("token")) { // is attempting reconnect?
                // reconnect here
                const token = queryParams.get("token");
                this.setState({
                    autoActionMode: 'reconnecting',
                    autoActionStatus: 'Reconnecting to your session...',
                });
                this.handleReconnect(token);
            } else if (queryParams.has("qrCode")) {
                // Pre-fill room code from the QR link.
                // If we already have a valid saved name, auto-submit.
                // Otherwise show the form pre-populated with the code (and
                // canJoin set via checkGameExists) so the user types a name
                // and hits Join — without checkGameExists firing here, the
                // submit button would stay disabled because canJoin is false.
                // Use savedNickname (synchronously available from localStorage
                // read above) rather than this.state.nickname — the setState
                // from the read is async and hasn't flushed yet.
                const qrCode = queryParams.get("qrCode");
                this.setState({ roomId: qrCode });
                if (isValidPlayerName(savedNickname)) {
                    await this.handleJoinRoom(qrCode);
                } else {
                    await this.checkGameExists(qrCode);
                }
            } else if (queryParams.has("joinRoom")) {
                // Coming back from external game (e.g., SongBlast) - auto-join the lobby room
                // Use retry logic since client may arrive before host creates the room
                const joinRoomCode = queryParams.get("joinRoom");
                const returnName = queryParams.get("name");
                const returnAvatar = queryParams.get("avatar");
                console.log("Auto-joining lobby room:", joinRoomCode, "name:", returnName, "avatar:", returnAvatar);

                // Set auto-action mode BEFORE starting the retry loop, and
                // wait for the setState to flush — the loop's cancellation
                // guard reads this.state.autoActionMode, so starting it before
                // the flush would make the first check see null and bail
                // immediately, stranding players on the loading screen.
                await new Promise((resolve) => this.setState({
                    autoActionMode: 'waiting_for_host',
                    autoActionStatus: 'Returning to lobby...',
                    roomId: joinRoomCode,
                    nickname: returnName || this.state.nickname,
                    returnAvatar,
                }, resolve));
                await this.waitForRoomAndJoin(joinRoomCode);
            }
        }
    }

    componentWillUnmount() {
        clearTimeout(this.autoActionWatchdog);
    }

    componentDidUpdate(prevProps, prevState) {
        const queryParams = new URLSearchParams(window.location.search);

        // Arm/disarm the give-up watchdog on auto-action mode transitions so
        // every entry point (token reconnect, joinRoom return) is covered.
        if (!prevState.autoActionMode && this.state.autoActionMode) {
            this.startAutoActionWatchdog();
        } else if (prevState.autoActionMode && !this.state.autoActionMode) {
            clearTimeout(this.autoActionWatchdog);
        }

        if (this.props.room.roomOpen === true && prevProps.room.roomOpen === false) {
            if (this.props.room.isMirror) {
                this.props.navigate("/lobby");
            } else if (queryParams.has("roku")) {
                this.props.navigate(`/play?roku`);
            } else {
                this.props.navigate(`/play`);
            }
        }
    }

    onChangeRoomId = async (e) => {
        this.setState({errorMessage: "", passwordProtected: false, externalRedirectUrl: null})
        const roomId = e.target.value.toUpperCase();

        if (roomId.length === 4) {
            this.setState({ roomId, });
            await this.checkGameExists(roomId);
        } else {
            this.setState({ canJoin: false, joinButtonValue: "JOIN", roomId });
        }
    };

    onChangeNickname = (e) => {
        this.setState({ errorMessage: "" })
        const re = /^[A-Za-z0-9 _]*[A-Za-z0-9][A-Za-z0-9 _]*$/;
        const nickname = e.target.value.toUpperCase();
        if (nickname === '' || re.test(nickname)) {
            this.setState({ nickname });
        }
    };

    onChangePIN = (e) => {
        this.setState({ errorMessage: "" })
        this.setState({ pin: e.target.value });
    }

    storeSessionId(id) {
        try {
            localStorage.setItem("sessionId", id);
        } catch (e) {
            console.log(e);
        }
        this.setState({ storedSessionId: id });
    }

    getSessionIdFromStorage() {
        try {
            return localStorage.getItem('sessionId');
        } catch (e) {
            console.log(e);
            return null;
        }
    }


    reloadPage = () => {
        window.location.reload(true);
    }


    async checkGameExists(roomId) {
        const storedSessionId = this.getSessionIdFromStorage();

        if (this.cancelToken != null) {
            this.cancelToken.cancel("Operation canceled due to new request.")
        }

        if (roomId != null) {
            this.cancelToken = axios.CancelToken.source();

            return await axios.get(process.env.REACT_APP_MATCH_MAKE_URL + `/game-exists/?roomId=${roomId}&sessionId=${storedSessionId}`,
                {
                    cancelToken: this.cancelToken.token,
                    // A hung request would stall waitForRoomAndJoin's retry
                    // loop indefinitely — bound each attempt instead.
                    timeout: 8000,
                }
            ).then(res => {
                console.log("Game exists check result");
                console.log(JSON.stringify(res));
                let errorMessage = "";

                if (res.data.gameExists) {
                    // Check if game is at an external server (e.g., Tenable, SongBlast)
                    // Server is the source of truth - if it says go to external game, go there
                    // EXCEPT when isReturning=true - that means players are returning to lobby
                    if (res.data.isExternal && res.data.redirectUrl && !res.data.isReturning) {
                        console.log(`Room ${roomId} is at external game: ${res.data.gameId} (${res.data.displayName})`);
                        console.log(`Redirecting to: ${res.data.redirectUrl}`);

                        if (this.state.nickname) {
                            // Has nickname — fast track redirect immediately
                            localStorage.setItem("nickname", this.state.nickname);
                            let redirectUrl = res.data.redirectUrl;
                            redirectUrl += `&name=${encodeURIComponent(this.state.nickname)}`;
                            window.location = redirectUrl;
                            return res.data;
                        } else {
                            // No nickname — hold on join page so player can enter a name first
                            console.log(`No nickname set, holding on join page for name entry`);
                            this.setState({
                                canJoin: true,
                                externalRedirectUrl: res.data.redirectUrl,
                                joinButtonValue: "Join",
                            });
                            return res.data;
                        }
                    }

                    // If isReturning=true, the external game is ending and players are
                    // going back to lobby - treat as "room not found yet" so retry logic works
                    if (res.data.isExternal && res.data.isReturning) {
                        console.log(`Room ${roomId} is returning from external game - waiting for lobby room`);
                        return { gameExists: false, isReturning: true };
                    }

                    errorMessage = "";
                    if (res.data.passwordProtected) {
                        this.setState({ passwordProtected: true, errorMessage: "Please enter room PIN", errorVariant: "warning" });
                    }

                    if (res.data.playerExists) {
                        this.setState({ joinButtonValue: "Reconnect", errorMessage, canJoin: true, });

                        if (res.data.token.length > 0) {
                            this.setState({ token: res.data.token })
                        }
                    } else {
                        this.setState({ canJoin: !res.data.gameFull, });
                        this.setState({ joinButtonValue: "Join", errorMessage, });
                    }
                } else {
                    errorMessage = `Room '${roomId}' does not exist...`;
                    this.setState({ canJoin: false, errorMessage, errorVariant: "danger" });
                }
                this.cancelToken = null;
                return res.data;
            }).catch((error) => {
                if (axios.isCancel(error)) {
                    console.log('Request canceled');
                }
                console.log({ error });

                this.setState({ connectionError: true });

                return null;
            });
        }
    }

    clickJoinRoom = async (e) => {
        e.preventDefault();
        await this.handleJoinRoom();
    }

    getCookie(name) {
        var nameEQ = name + "=";
        var ca = document.cookie.split(';');
        for (var i = 0; i < ca.length; i++) {
            var c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
        }
        return null;
    }

    async handleJoinRoom(override = null) {
        // External game redirect — player has now entered a name, do the redirect.
        // Same name validation as the regular join path: must be non-empty and
        // not the legacy "PLAYER" placeholder.
        if (this.state.externalRedirectUrl) {
            if (!isValidPlayerName(this.state.nickname)) {
                const wasPlaceholder = this.state.nickname
                    && String(this.state.nickname).trim().toUpperCase() === "PLAYER";
                this.setState({
                    nickname: wasPlaceholder ? "" : this.state.nickname,
                    errorMessage: "Please enter your name to join.",
                    errorVariant: "warning",
                });
                return;
            }
            const name = this.state.nickname.trim();
            localStorage.setItem("nickname", name);
            let redirectUrl = this.state.externalRedirectUrl;
            redirectUrl += `&name=${encodeURIComponent(name)}`;
            window.location = redirectUrl;
            return;
        }

        if (this.state.joinButtonValue === "Reconnect") {
            this.handleReconnect(this.state.token);
        } else {
            let roomId = override ? override : this.state.roomId;
            const gameCheckData = await this.checkGameExists(roomId);

            if (!this.state.loading) {
                if (gameCheckData != null && gameCheckData.playerExists) {
                    let token = gameCheckData.token;
                    this.handleReconnect(token);
                } else {
                    if (gameCheckData.passwordProtected) {
                        if (this.state.pin?.length === 0) {
                            this.setState({ errorMessage: "Please enter room PIN", errorVariant: "warning" })
                            return;
                        }
                    }

                    this.joinRoom(roomId);
                }
            }
        }
    };

    /**
     * Wait for a room to be created and then join it.
     * Used when returning from external games - client may arrive before host creates the room.
     */
    async waitForRoomAndJoin(roomCode, maxRetries = 60, retryDelay = 1000) {
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            // Stop if the player hit "Enter code manually" or the watchdog gave
            // up — otherwise this loop keeps running behind the form and could
            // yank the player into the room mid-typing.
            if (!this.state.autoActionMode) return;
            console.log(`[waitForRoomAndJoin] Attempt ${attempt}/${maxRetries} for room ${roomCode}`);

            const gameCheckData = await this.checkGameExists(roomCode);

            if (gameCheckData?.gameExists && !gameCheckData?.isExternal) {
                // Local room exists, proceed to join
                console.log(`[waitForRoomAndJoin] Room ${roomCode} found, joining...`);
                // Keep autoActionMode active - update status to show we're joining
                this.setState({ autoActionStatus: 'Joining room...' });
                await this.handleJoinRoom(roomCode);
                return;
            }

            // Check if room is in 'returning' state (external game ending)
            const isReturning = gameCheckData?.isReturning;

            if (attempt < maxRetries) {
                this.setState({
                    autoActionStatus: pickWaitingMessage(attempt, isReturning),
                });
                await new Promise(resolve => setTimeout(resolve, retryDelay));
            }
        }

        // All retries exhausted, room still doesn't exist - exit auto-action mode and show form
        console.log(`[waitForRoomAndJoin] Room ${roomCode} not found after ${maxRetries} attempts`);
        this.setState({
            autoActionMode: null,     // Exit auto-action mode
            loading: false,
            errorMessage: `Room '${roomCode}' was not created. The host may have encountered an issue.`,
            errorVariant: "danger",
            canJoin: false,
        });
    }

    joinRoom = (roomID, roomPin = null) => {
        console.log("Joining room:", roomID, roomPin, this.state.pin)

        // Name validation gate — empty or the legacy "PLAYER" placeholder both
        // fail. On "PLAYER" specifically, clear the input so the user sees the
        // form refresh and types a real name. Don't proceed to the join.
        if (!isValidPlayerName(this.state.nickname)) {
            const wasPlaceholder = this.state.nickname
                && String(this.state.nickname).trim().toUpperCase() === "PLAYER";
            // If we were called from the auto-join modal (driven by QRSessionID
            // after a login QR scan), the modal is still mounted over the form.
            // Close it and pre-fill the room code so the user can finish the
            // join manually once they type a nickname. Mirrors the qrCode-
            // no-name path in componentDidMount (setState roomId + checkGameExists).
            const cameFromAutoJoin = !!this.state.QRSessionID;
            this.setState({
                nickname: wasPlaceholder ? "" : this.state.nickname,
                errorMessage: "Please enter your name to join.",
                errorVariant: "warning",
                loading: false,
                // If we got here from the auto-join screen (returning from an
                // external game without a usable name), drop to the form —
                // staying in auto-action mode would strand the player on
                // "Returning to lobby" forever.
                autoActionMode: null,
                ...(cameFromAutoJoin && {
                    QRSessionID: null,
                    roomId: roomID,
                }),
            });
            if (cameFromAutoJoin) {
                this.checkGameExists(roomID);
            }
            return;
        }

        this.setState({ loading: true, errorMessage: "", error: false, });
        let options = {};
        const name = this.state.nickname.trim();
        options.name = name;

        // store in local storage
        try {
            localStorage.setItem("nickname", name);
        } catch (e) {
            console.log(e);
        }

        // Pass avatar URL if returning from external game (e.g., SongBlast)
        if (this.state.returnAvatar) {
            options.avatarSrc = this.state.returnAvatar;
            console.log("Passing avatar from external game:", options.avatarSrc);
        }

        console.log("Password Protected : ", this.state.passwordProtected)
        if (this.state.passwordProtected) {
            options.password = roomPin ? roomPin : this.state.pin;
        } else if (roomPin) {
            options.password = roomPin;
        }
        console.log("options password : ", options.password);

        this.props.joinRoom(roomID, options).then((res) => {
            if (res.Status === "Error") {
                // Exit auto-action mode to show the form with error
                this.setState({
                    autoActionMode: null,
                    loading: false,
                    errorMessage: res.Message,
                    errorVariant: "danger"
                });
            } else if (res.Status === "Success") {
                console.log("Join Success");
                this.storeSessionId(res.room.sessionId);
                // Navigation happens in componentDidUpdate when roomOpen becomes true
            }
        });
    }


    handleReconnect(token) {
        // Extract roomId from token by splitting on ':' (format: ROOMCODE:sessionId-V3)
        const [roomId] = token && token.includes(':') ? token.split(':') : [this.state.roomId];

        if (!token) {
            this.setState({
                autoActionMode: null,     // Exit auto-action mode
                errorMessage: "There was a problem reconnecting to the room.",
                errorVariant: "danger",
                reconnectFailed: true,    // Force "Join" button
                roomId,                   // Pre-fill room code for fresh join attempt
            });
            return;
        }

        this.setState({ loading: true, errorMessage: "", error: false, roomId });

        setTimeout(() => {
            this.props.reconnectToRoom(token).then(async (res) => {
                if (res.Status === "Error") {
                    this.setState({
                        autoActionMode: null,     // Exit auto-action mode
                        loading: false,
                        errorMessage: res.Message,
                        errorVariant: "danger",
                        token: null,
                        joinButtonValue: "Join",
                        reconnectFailed: true,    // Force "Join" button
                        roomId,                   // Pre-fill room code for fresh join attempt
                    });
                    // Check if room exists to enable the Join button
                    if (roomId && roomId.length === 4) {
                        await this.checkGameExists(roomId);
                        // Force "Join" button since reconnect just failed (checkGameExists may set "Reconnect")
                        this.setState({ joinButtonValue: "Join", token: null });
                    }
                } else if (res.Status === "Success") {
                    console.log("res , ", res);
                }
            });
        }, 1500);
    }

    clickJoinAsMirror = async (e) => {
        e.preventDefault();
        if (!this.state.canJoin || this.state.loading) return;

        if (this.state.passwordProtected && this.state.pin.length === 0) {
            this.setState({ errorMessage: "Please enter room PIN", errorVariant: "warning" });
            return;
        }

        this.setState({ loading: true, errorMessage: "" });
        const password = this.state.passwordProtected ? this.state.pin : null;
        const result = await this.props.joinAsMirror(this.state.roomId, password);
        if (result.Status === "Error") {
            this.setState({
                loading: false,
                errorMessage: result.Message || "Failed to join as mirror.",
                errorVariant: "danger",
            });
        }
        // On success, componentDidUpdate navigates to /lobby
    };

    cancelAutoAction = () => {
        this.setState({
            autoActionMode: null,
            autoActionStatus: '',
            loading: false,
        });
    }

    startAutoActionWatchdog = () => {
        clearTimeout(this.autoActionWatchdog);
        this.autoActionWatchdog = setTimeout(() => {
            if (!this.state.autoActionMode) return;
            const { roomId } = this.state;
            this.setState({
                autoActionMode: null,
                autoActionStatus: '',
                loading: false,
                errorMessage: "We couldn't get you back in automatically. Enter the room code to join.",
                errorVariant: "warning",
                reconnectFailed: true,
            });
            // Room code carried over from the auto attempt — validate it so the
            // Join button enables without the player retyping the code.
            if (roomId && roomId.length === 4) this.checkGameExists(roomId);
        }, AUTO_ACTION_TIMEOUT_MS);
    }

    renderAutoActionScreen() {
        const { autoActionMode, roomId } = this.state;

        // One sentence carries everything: what's happening and which room.
        const verb = autoActionMode === 'reconnecting' ? 'Reconnecting you to' : 'Returning you to';
        const target = autoActionMode === 'reconnecting' ? 'the game' : 'the lobby';

        return (
            <div className={styles.autoActionScreen}>
                <div className={styles.cityScape} />
                <div className={styles.cloudBG} />
                <div className={styles.autoActionContent}>
                    <img src={LogoWhite} className={styles.gcLogo} alt="Game City" />
                    <h2 className={styles.autoActionTitle}>{verb} {target}</h2>
                    <span className={styles.dots} aria-hidden="true">
                        <span /><span /><span />
                    </span>
                    {this.state.autoActionStatus && (
                        <p className={styles.autoActionStatus}>{this.state.autoActionStatus}</p>
                    )}
                    <button className={styles.cancelButton} onClick={this.cancelAutoAction}>
                        Enter code manually
                    </button>
                </div>
                {roomId && <div className={styles.roomFooter}>Room {roomId}</div>}
            </div>
        );
    }

    render() {
        //const player = this.props.room.player;
        return (
            <Container>
                <PageSEO {...SEO_CONFIG['/join']} />
                {
                    this.state.checkingRoomOpened ? (
                        <DotLoader size="medium" variant="light" />
                    ) : this.state.autoActionMode ? (
                        this.renderAutoActionScreen()
                    ) : (
                        <div>
                            <h1 className={formStyles.formTitle}>{SEO_CONFIG['/join'].h1}</h1>
                            <Form onSubmit={this.clickJoinRoom} >
                                <p>To join a room enter the Room Code displayed on your host's device.<br />Or simply scan the QR Code.</p>
                                <Form.Group className="mb-3" controlId="formBasicEmail">
                                    <Form.Label>Room Code</Form.Label>
                                    <Form.Control type="text" autoComplete="off" maxLength={4} placeholder="Enter Room Id" style={{ textTransform: "uppercase" }} value={this.state.roomId} required onChange={this.onChangeRoomId} />

                                </Form.Group>
                                <Form.Group className="mb-3" controlId="formBasicEmail">
                                    <Form.Label>Nickname</Form.Label>
                                    <Form.Control type="text" autoComplete="off" placeholder="Enter Nickname" style={{ textTransform: "uppercase" }} value={this.state.nickname} maxLength={10} minLength={2} required={this.state.joinButtonValue !== "Reconnect"} onChange={this.onChangeNickname} />
                                </Form.Group>
                                {
                                    this.state.passwordProtected &&
                                    <Form.Group className="mb-3" controlId="formBasicEmail">
                                        <Form.Label>Room PIN</Form.Label>
                                        <Form.Control type="password" autoComplete="off" placeholder="Enter Room PIN" style={{ textTransform: "uppercase" }} value={this.state.pin} maxLength={8} minLength={4} required={this.state.joinButtonValue !== "Reconnect"} onChange={this.onChangePIN} />
                                    </Form.Group>
                                }
                                <button className={formStyles.button} disabled={!this.state.canJoin} variant="primary" type="submit">
                                    {this.state.loading ? <DotLoader size="medium" variant="light" /> : this.state.joinButtonValue}
                                </button>
                                <FormAlert variant="danger">{this.state.errorMessage}</FormAlert>
                            </Form>
                        </div>
                    )
                }
                {
                    this.state.QRSessionID && <AutoJoinModal onComplete={this.joinRoom} QRSessionID={this.state.QRSessionID} />
                }
                { this.state.connectionError &&
                            <ErrorModal
                    title={"Disconnected"}
                    styles={"d-flex"}
                    message={"There is a problem connecting to the lobby. Please refresh and try again, if the problem persists it may be a firewall blocking your attempts."}
                    callback={this.reloadPage}
                    callbackText={"Try again" }
                            />
                 }
            </Container>
        );
    }
}


const mapStateToProps = (state) => {
    return {
        auth: state.auth,
        room: state.room,
    };
}


const mapDispatchToProps = (dispatch) => {
    return {
        joinRoom: (roomId, options) => {
            return dispatch(joinRoom(roomId, options))
        },
        reconnectToRoom: (token) => {
            return dispatch(reconnectToRoom(token))
        },
        joinAsMirror: (roomId, password) => {
            return dispatch(joinAsMirror(roomId, password))
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Join));
